import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { api, errMsg } from "../../lib/api";

const PRESETS = {
  Diwali: { title: "Diwali flower bookings are open", message: "Light up your home with fresh marigold thoranams, door décor and pooja garlands this Diwali. Book early — three days prior booking is required for home delivery." },
  Varamahalakshmi: { title: "Varamahalakshmi pooja bookings are open", message: "Fresh Thomala, garlands and mini décor for your Varamahalakshmi Vratham. Book early to reserve your design — three days prior booking is required." },
};
const field = "rounded-xl border-[#D4AF37]/25 bg-[#190317]/60 text-[#FFF8EF] placeholder:text-[#E8D8C8]/60";
const EMPTY = { enabled: false, title: "", message: "", start_date: "", end_date: "" };

export const FestivalPanel = () => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-festival"], queryFn: () => api.get("/admin/festival").then((r) => r.data) });
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (data) setF({ ...EMPTY, ...data, start_date: data.start_date || "", end_date: data.end_date || "" });
  }, [data]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/festival", { ...f, start_date: f.start_date || null, end_date: f.end_date || null });
      qc.invalidateQueries({ queryKey: ["admin-festival"] });
      qc.invalidateQueries({ queryKey: ["festival"] });
      toast.success(f.enabled ? "Banner saved and switched on" : "Banner saved (switched off)");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <Loader2 className="animate-spin text-[#F3E5AB]" />;
  return (
    <div className="max-w-2xl space-y-5 rounded-[24px] border border-[#D4AF37]/20 bg-[#330B2E]/70 p-6" data-testid="festival-panel">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-3xl">Festival banner</h2>
          <p className="text-sm text-[#E8D8C8]">Shows on the home page below the main banner while switched on and within the dates.</p>
        </div>
        <Switch checked={f.enabled} onCheckedChange={(v) => setF((s) => ({ ...s, enabled: v }))} data-testid="festival-enabled-switch" />
      </div>
      <div className="flex flex-wrap gap-2">
        <span className="self-center text-sm text-[#E8D8C8]">Start from:</span>
        {Object.keys(PRESETS).map((k) => (
          <button key={k} onClick={() => setF((s) => ({ ...s, ...PRESETS[k] }))} className="rounded-full border border-[#D4AF37]/40 px-4 py-1.5 text-sm text-[#F3E5AB] hover:bg-[#D4AF37]/10" data-testid={`festival-preset-${k.toLowerCase()}`}>{k}</button>
        ))}
      </div>
      <Input value={f.title} onChange={set("title")} placeholder="Banner title" className={`h-12 ${field}`} data-testid="festival-title-input" />
      <Textarea value={f.message} onChange={set("message")} placeholder="Message" rows={3} className={field} data-testid="festival-message-input" />
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="text-sm text-[#E8D8C8]">Show from<Input type="date" value={f.start_date} onChange={set("start_date")} className={`mt-1 h-12 ${field} [color-scheme:dark]`} data-testid="festival-start-input" /></label>
        <label className="text-sm text-[#E8D8C8]">Show until (book-by date)<Input type="date" value={f.end_date} onChange={set("end_date")} className={`mt-1 h-12 ${field} [color-scheme:dark]`} data-testid="festival-end-input" /></label>
      </div>
      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-gold-foil px-6 py-3 text-sm font-semibold text-[#240620] disabled:opacity-60" data-testid="festival-save-btn">
        {saving && <Loader2 size={15} className="animate-spin" />} Save banner
      </button>
    </div>
  );
};
